// Feature-level controller over `useLineItemPatch`/`useCreateCategory`
// (spec §11 slice 3): the single place that owns a line item's optimistic
// category write and version serialization. `FeedRow`, `TransactionDetailPanel`,
// and editable Inbox rows all call this instead of duplicating the same
// PATCH/error logic inline.
//
// Founder revision §14 decision 3 (2026-09-08): the bottom-right tray is
// gone. This hook is now the ONLY owner of a line item's correction
// lifecycle -- Undo is a small inline affordance next to the changed
// control, and "apply to all items from {merchant}" is a checkbox the
// caller (the scope menu / TypeChooser footer) sets on the SAME write
// rather than a follow-up offer surfaced elsewhere. A confirmed write's
// `apply_to_similar` block is remembered here (`lastKnownCounts`) so a
// LATER open of the same line item's scope menu/chooser can show a real
// count -- never fabricated, never shown before a write has returned one.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useLineItemPatch } from "@/api/hooks/useLineItemPatch";
import { useCreateCategory } from "@/api/hooks/useCategoryMutations";
import { useApplyToSimilar } from "@/api/hooks/useApplyToSimilar";
import {
  emitCategoryUndo,
  emitScopeFlip,
  emitMerchantApplyOutcome,
  emitScopeFlipResult,
  emitTypeChange,
  emitTypeChangeResult,
  emitTypeChooserAdd,
  newInteractionId,
  queryLengthBucket,
  toErrorClass,
  type TelemetryContext,
} from "./telemetry";

/**
 * No response at all (timeout/network) is an UNKNOWN outcome, never a
 * definitive rejection (§7/§9).
 */
function isDefinitiveRejection(error: unknown): boolean {
  if (isAxiosError(error)) return error.response !== undefined;
  return true;
}

/**
 * Data deferrable 3: a count only counts if it is a finite, non-negative
 * integer -- the same guard `lastKnownCounts` already applies to `all_time`
 * below, now shared with `applied_count` too, so an untrusted/malformed
 * count can never render as a real number.
 */
function validCount(value: number): number | null {
  return Number.isFinite(value) && Number.isInteger(value) && value >= 0 ? value : null;
}

export type ApplyCategoryTelemetry =
  | {
      kind: "scope_flip";
      context: TelemetryContext;
      fromScope: "personal" | "business";
      toScope: "personal" | "business";
      fromTypeId: string | null;
      toTypeId: string | null;
      mapping: "counterpart" | "catch_all";
    }
  | {
      kind: "type_change";
      context: TelemetryContext;
      fromTypeId: string | null;
      toTypeId: string;
      fromScope: "personal" | "business" | null;
      toScope: "personal" | "business";
      selection: "suggested" | "search" | "browse" | "created";
    };

/** The destination pair to paint optimistically while a write is in flight (§3 "Saving correction"). */
export interface SavingTarget {
  categoryId: string;
  categoryLabel: string;
  scope: "personal" | "business" | null;
}

/** The compensating-PATCH target for the inline "Undo" affordance. */
export interface UndoTarget {
  lineItemId: string;
  /** The version the server just acknowledged -- Undo's PATCH must use THIS, never a locally incremented guess. */
  acknowledgedVersion: number;
  previousCategoryId: string | null;
  previousIsBusiness: boolean | null;
}

/** The real all-time eligible count from a PATCH response -- never estimated (§14 decision 3). */
export interface MerchantApplyCounts {
  merchantId: string;
  merchantDisplay: string;
  allTime: number;
}

export type MerchantApplyStatus =
  | { kind: "idle" }
  | { kind: "applying" }
  // D-2: apply-to-similar applies synchronously -- by the time this state
  // is reached the relabel has already happened server-side, not merely
  // been queued. `appliedCount` is the real count of OTHER items relabeled
  // (never the item just corrected directly via PATCH).
  | { kind: "applied"; appliedCount: number }
  | { kind: "failed" }
  | { kind: "unconfirmed" }
  /**
   * ENG-R1-002: the user checked "Apply to all items from {merchant}" but
   * the PATCH response's `apply_to_similar` came back `null` -- this
   * transaction has no identified merchant, so there is nothing to apply
   * to. Rendering nothing here (the old behavior) was a silent no-op the
   * user has no way to notice (AGENTS.md Data & UI honesty 2) -- this is
   * the honest "we didn't do what you asked, and here's why" state, never a
   * fabricated match count.
   */
  | { kind: "no_merchant" };

export interface SavedInfo {
  undo: UndoTarget | null;
  undoStatus: "idle" | "pending" | "failed" | "unconfirmed";
  merchantApply: MerchantApplyStatus;
  originalInteractionId: string | null;
  context: TelemetryContext | null;
}

export type CorrectionState =
  | { kind: "idle" }
  | { kind: "saving"; target: SavingTarget }
  | { kind: "changed_since" }
  | { kind: "rejected"; retry: () => void }
  | { kind: "unconfirmed" }
  | { kind: "creating" }
  | { kind: "create_failed" }
  | {
      kind: "created_apply_failed";
      createdCategoryId: string;
      createdCategoryLabel: string;
      retry: () => void;
    }
  | { kind: "saved"; info: SavedInfo };

export function useCategoryCorrection() {
  const [state, setState] = useState<CorrectionState>({ kind: "idle" });
  const [lastKnownCounts, setLastKnownCounts] = useState<MerchantApplyCounts | null>(null);
  const queryClient = useQueryClient();
  const patch = useLineItemPatch();
  const createCategory = useCreateCategory();
  const applyToSimilar = useApplyToSimilar();

  /**
   * Applies a category (and, when known, its `is_business`) to one line
   * item. `previousCategoryId`/`previousIsBusiness` are the CONFIRMED prior
   * values (before this write) -- required so a later Undo restores the
   * real prior state, not a guess. `applyToMerchant` (§14 decision 3): when
   * true and the PATCH response carries a real merchant, the apply-to-similar
   * (`all_time`) POST follows the PATCH on success and relabels every
   * matching item SYNCHRONOUSLY (D-2) -- no job is enqueued or registered.
   */
  function applyCategory(params: {
    lineItemId: string;
    version: number;
    categoryId: string;
    isBusiness?: boolean;
    categoryLabel: string;
    merchant: string | null;
    previousCategoryId: string | null;
    previousIsBusiness: boolean | null;
    transactionId?: string | null;
    /** §9: when present, emits the attempt event now and the matching result event from onSuccess/onError, sharing one interaction_id. */
    telemetry?: ApplyCategoryTelemetry;
    /**
     * The follow-up apply-to-similar POST fires on PATCH success when this
     * transaction has a real merchant. When the PATCH's `apply_to_similar`
     * is `null` (no merchant), this is a no-op -- never fabricate a rule
     * target.
     */
    applyToMerchant?: boolean;
    /**
     * Present only for the auto-apply PATCH that follows a just-created
     * category. A failure here (other than a version conflict) routes to
     * `created_apply_failed` instead of `rejected`/`unconfirmed`.
     */
    createdCategory?: { id: string; label: string };
  }) {
    function attempt() {
      const targetScope: "personal" | "business" | null =
        params.telemetry?.toScope ?? (params.isBusiness === undefined ? null : params.isBusiness ? "business" : "personal");
      setState({
        kind: "saving",
        target: { categoryId: params.categoryId, categoryLabel: params.categoryLabel, scope: targetScope },
      });

      const telemetry = params.telemetry;
      const interactionId = telemetry ? newInteractionId() : null;
      const startedAt = telemetry ? Date.now() : 0;
      if (telemetry && interactionId) {
        if (telemetry.kind === "scope_flip") {
          emitScopeFlip(telemetry.context, interactionId, {
            line_item_id: params.lineItemId,
            from_scope: telemetry.fromScope,
            to_scope: telemetry.toScope,
            from_type_id: telemetry.fromTypeId,
            to_type_id: telemetry.toTypeId,
            mapping: telemetry.mapping,
            version: params.version,
            apply_to_merchant: !!params.applyToMerchant,
          });
        } else {
          emitTypeChange(telemetry.context, interactionId, {
            line_item_id: params.lineItemId,
            from_type_id: telemetry.fromTypeId,
            to_type_id: telemetry.toTypeId,
            from_scope: telemetry.fromScope,
            to_scope: telemetry.toScope,
            selection: telemetry.selection,
            apply_to_merchant: !!params.applyToMerchant,
          });
        }
      }

      function emitResult(outcome: "saved" | "conflict" | "rejected" | "unconfirmed", errorClass: ReturnType<typeof toErrorClass> | null) {
        if (!telemetry || !interactionId) return;
        const latency_ms = Date.now() - startedAt;
        if (telemetry.kind === "scope_flip") {
          emitScopeFlipResult(telemetry.context, interactionId, { outcome, latency_ms, error_class: errorClass });
        } else {
          emitTypeChangeResult(telemetry.context, interactionId, {
            outcome,
            latency_ms,
            created_type: telemetry.selection === "created",
            error_class: errorClass,
          });
        }
      }

      patch.mutate(
        {
          lineItemId: params.lineItemId,
          input: {
            version: params.version,
            category_id: params.categoryId,
            ...(params.isBusiness !== undefined
              ? { is_business: params.isBusiness }
              : {}),
          },
        },
        {
          onSuccess: (result) => {
            const undo: UndoTarget = {
              lineItemId: params.lineItemId,
              acknowledgedVersion: result.line_item.version,
              previousCategoryId: params.previousCategoryId,
              previousIsBusiness: params.previousIsBusiness,
            };
            const raw = result.apply_to_similar;
            if (
              raw &&
              typeof raw.all_time === "number" &&
              Number.isInteger(raw.all_time) &&
              raw.all_time >= 0
            ) {
              setLastKnownCounts({
                merchantId: raw.merchant_id,
                merchantDisplay: raw.merchant_display,
                allTime: raw.all_time,
              });
            }
            const willApplyToMerchant = !!params.applyToMerchant && !!raw;
            // ENG-R1-002: the user asked to apply to the merchant, but this
            // transaction's PATCH came back with no merchant block --
            // `no_merchant`, never a silent `idle`.
            const requestedButNoMerchant = !!params.applyToMerchant && !raw;
            setState({
              kind: "saved",
              info: {
                undo,
                undoStatus: "idle",
                merchantApply: willApplyToMerchant
                  ? { kind: "applying" }
                  : requestedButNoMerchant
                    ? { kind: "no_merchant" }
                    : { kind: "idle" },
                originalInteractionId: interactionId,
                context: telemetry?.context ?? null,
              },
            });
            emitResult("saved", null);
            if (requestedButNoMerchant && telemetry) {
              emitMerchantApplyOutcome(telemetry.context, newInteractionId(), {
                outcome: "no_merchant",
                applied_count: null,
                latency_ms: 0,
                error_class: null,
              });
            }

            if (willApplyToMerchant) {
              const applyStartedAt = telemetry ? Date.now() : 0;
              const applyInteractionId = telemetry ? newInteractionId() : null;
              applyToSimilar.mutate(
                {
                  lineItemId: params.lineItemId,
                  scope: "all_time",
                  categoryId: params.categoryId,
                  isBusiness: params.isBusiness,
                },
                {
                  onSuccess: (applyResult) => {
                    // D-2: no job is enqueued for apply-to-similar (a
                    // merchant rule always fixes the leaf synchronously) --
                    // `registerJob` is not called for this path.
                    //
                    // Guard `applied_count` with the same integer/non-negative
                    // check `lastKnownCounts` already applies to `all_time`
                    // above -- an untrusted or malformed count must never
                    // render as a real number (Data deferrable 3).
                    const applied = validCount(applyResult.applied_count);
                    setState((prev) =>
                      prev.kind === "saved"
                        ? {
                            ...prev,
                            info: {
                              ...prev.info,
                              merchantApply:
                                applied !== null
                                  ? { kind: "applied", appliedCount: applied }
                                  : { kind: "unconfirmed" },
                            },
                          }
                        : prev,
                    );
                    if (telemetry && applyInteractionId) {
                      emitMerchantApplyOutcome(telemetry.context, applyInteractionId, {
                        outcome: applied !== null ? "applied" : "unconfirmed",
                        applied_count: applied,
                        latency_ms: Date.now() - applyStartedAt,
                        error_class: null,
                      });
                    }
                  },
                  onError: (error) => {
                    const definitive = isDefinitiveRejection(error);
                    setState((prev) =>
                      prev.kind === "saved"
                        ? {
                            ...prev,
                            info: {
                              ...prev.info,
                              merchantApply: { kind: definitive ? "failed" : "unconfirmed" },
                            },
                          }
                        : prev,
                    );
                    if (telemetry && applyInteractionId) {
                      emitMerchantApplyOutcome(telemetry.context, applyInteractionId, {
                        outcome: definitive ? "failed" : "unconfirmed",
                        applied_count: null,
                        latency_ms: Date.now() - applyStartedAt,
                        error_class: toErrorClass(error),
                      });
                    }
                  },
                },
              );
            }
          },
          onError: (error) => {
            if (error.code === "changed_since") {
              setState({ kind: "changed_since" });
              emitResult("conflict", toErrorClass(error));
              return;
            }
            const definitive = isDefinitiveRejection(error);
            if (params.createdCategory) {
              setState({
                kind: "created_apply_failed",
                createdCategoryId: params.createdCategory.id,
                createdCategoryLabel: params.createdCategory.label,
                retry: attempt,
              });
            } else if (definitive) {
              setState({ kind: "rejected", retry: attempt });
            } else {
              setState({ kind: "unconfirmed" });
            }
            emitResult(definitive ? "rejected" : "unconfirmed", toErrorClass(error));
          },
        },
      );
    }
    attempt();
  }

  /**
   * Single-item reversal (§4.1, carried unchanged by §14): a compensating
   * PATCH using the ACKNOWLEDGED version, never a locally incremented
   * guess. Available while `state.kind === 'saved' && state.info.undo`.
   */
  function undoLast() {
    setState((prev) => {
      if (prev.kind !== "saved" || !prev.info.undo || prev.info.undoStatus === "pending") return prev;
      const target = prev.info.undo;
      const context = prev.info.context;
      const originalInteractionId = prev.info.originalInteractionId;
      const undoInteractionId = newInteractionId();
      if (context && originalInteractionId) {
        emitCategoryUndo(context, undoInteractionId, {
          stage: "attempt",
          original_interaction_id: originalInteractionId,
          line_item_id: target.lineItemId,
        });
      }
      patch.mutate(
        {
          lineItemId: target.lineItemId,
          input: {
            version: target.acknowledgedVersion,
            category_id: target.previousCategoryId,
            ...(target.previousIsBusiness !== null ? { is_business: target.previousIsBusiness } : {}),
          },
        },
        {
          onSuccess: () => {
            if (context && originalInteractionId) {
              emitCategoryUndo(context, undoInteractionId, {
                stage: "saved",
                original_interaction_id: originalInteractionId,
                line_item_id: target.lineItemId,
              });
            }
            setState({ kind: "idle" });
          },
          onError: (error) => {
            const conflict = error.code === "changed_since";
            const definitive = isDefinitiveRejection(error);
            if (context && originalInteractionId) {
              emitCategoryUndo(context, undoInteractionId, {
                stage: conflict ? "conflict" : definitive ? "failed" : "unconfirmed",
                original_interaction_id: originalInteractionId,
                line_item_id: target.lineItemId,
              });
            }
            setState((prevState) =>
              prevState.kind === "saved"
                ? {
                    ...prevState,
                    info: { ...prevState.info, undoStatus: definitive ? "failed" : "unconfirmed" },
                  }
                : prevState,
            );
          },
        },
      );
      return { ...prev, info: { ...prev.info, undoStatus: "pending" } };
    });
  }

  function createAndApply(params: {
    parentId: string;
    name: string;
    lineItemId: string;
    version: number;
    merchant: string | null;
    previousCategoryId: string | null;
    previousIsBusiness: boolean | null;
    transactionId?: string | null;
    applyToMerchant?: boolean;
    telemetry?: {
      context: TelemetryContext;
      fromTypeId: string | null;
      fromScope: "personal" | "business" | null;
      toScope: "personal" | "business";
    };
    /**
     * Called once the category itself is confirmed created -- independent
     * of whether the follow-up apply-to-this-item PATCH succeeds. The
     * caller (TypeChooser's host) uses this to close the chooser popover.
     */
    onCreated?: () => void;
  }) {
    setState({ kind: "creating" });
    const addTelemetry = params.telemetry;
    const addInteractionId = addTelemetry ? newInteractionId() : null;
    if (addTelemetry && addInteractionId) {
      emitTypeChooserAdd(addTelemetry.context, addInteractionId, {
        stage: "submit",
        parent_id: params.parentId,
        name_length_bucket: queryLengthBucket(params.name.length),
        error_class: null,
      });
    }
    createCategory.mutate(
      { parent_id: params.parentId, name: params.name },
      {
        onSuccess: (created) => {
          if (addTelemetry && addInteractionId) {
            emitTypeChooserAdd(addTelemetry.context, addInteractionId, {
              stage: "created",
              parent_id: params.parentId,
              name_length_bucket: queryLengthBucket(params.name.length),
              error_class: null,
            });
          }
          params.onCreated?.();
          applyCategory({
            lineItemId: params.lineItemId,
            version: params.version,
            categoryId: created.category_id,
            isBusiness: created.is_business,
            categoryLabel: created.name,
            merchant: params.merchant,
            previousCategoryId: params.previousCategoryId,
            previousIsBusiness: params.previousIsBusiness,
            transactionId: params.transactionId,
            applyToMerchant: params.applyToMerchant,
            createdCategory: { id: created.category_id, label: created.name },
            telemetry: addTelemetry
              ? {
                  kind: "type_change",
                  context: addTelemetry.context,
                  fromTypeId: addTelemetry.fromTypeId,
                  toTypeId: created.category_id,
                  fromScope: addTelemetry.fromScope,
                  toScope: addTelemetry.toScope,
                  selection: "created",
                }
              : undefined,
          });
        },
        onError: (error) => {
          setState({ kind: "create_failed" });
          if (addTelemetry && addInteractionId) {
            emitTypeChooserAdd(addTelemetry.context, addInteractionId, {
              stage: isDefinitiveRejection(error) ? "failed" : "unconfirmed",
              parent_id: params.parentId,
              name_length_bucket: queryLengthBucket(params.name.length),
              error_class: toErrorClass(error),
            });
          }
        },
      },
    );
  }

  function reload(transactionScopeQueryKey: readonly unknown[] = ["transactions"]) {
    queryClient.invalidateQueries({ queryKey: transactionScopeQueryKey as string[] });
    setState({ kind: "idle" });
  }

  function dismiss() {
    setState({ kind: "idle" });
  }

  return {
    state,
    lastKnownCounts,
    applyCategory,
    createAndApply,
    undoLast,
    reload,
    dismiss,
    saving: patch.isPending || createCategory.isPending || applyToSimilar.isPending,
  };
}

export default useCategoryCorrection;
